import { ScoreType } from 'forms/post-score/types'
import {
  MatchUpSubmissionStateEnum,
  RatingSourceEnum,
  RatingTypeEnum,
  SexEnum,
  SideEnum,
  SignUpTypeEnum,
  WhereDidYouHearOptionEnum,
} from './api/enums'
import { BiographicalInformation, PersonAPI } from './api/types'

import { TypeOptions } from 'react-toastify'
import { AddOrManagePlayerModalMode } from 'constants/modals'
import { MessageTypes } from 'constants/message-screens'

export interface SearchOperators {
  normalized: string
}

export interface SearchFilterOptions {
  term: string
  fuzzy?: boolean
  autocomplete?: boolean
}

export interface NumberRange {
  start: number
  end: number
}

export interface NumberOperators {
  eq?: number
  ne?: number
  lt?: number
  lte?: number
  gt?: number
  gte?: number
  between?: NumberRange
}

export interface RangeNumberOperators {
  lt?: number
  lte?: number
  gt?: number
  gte?: number
}

export interface StringOperators {
  eq?: string
  contains?: string
  in?: string[]
}

export interface RatingFilterOptions {
  confidence?: number
  rating?: NumberOperators
  source?: RatingSourceEnum
  type?: RatingTypeEnum
  updatedAt?: string
}

export interface PublicPersonRatingFilterOptions {
  type: RatingTypeEnum
  rating?: RangeNumberOperators
  confidence?: NumberOperators
}

export interface PersonFilterOptions {
  age?: number
  createdAt?: string
  ids?: PersonIDInput[]
  ignoreProviderPersons?: boolean
  involvedInMatchUps?: boolean
  lastPlayedDoubles?: string
  lastPlayedSingles?: string
  nationalityCode?: StringOperators
  owningProviderId?: string
  providerId?: string
  providerNationalityPersons?: boolean
  ratings?: RatingFilterOptions
  ratingsMulti?: RatingFilterOptions[]
  search?: SearchOperators
  sex?: SexEnum
  updatedAt?: string
  scopeForSelfSubmission?: boolean
}

export interface PublicPersonFilterOptions {
  age?: RangeNumberOperators
  birthYear?: number
  nationalityCodes?: string[]
  ratings?: PublicPersonRatingFilterOptions[]
  search?: SearchFilterOptions
  sex?: SexEnum
  scopeForSelfSubmission?: boolean
}

export interface Paging {
  skip?: number
  limit?: number
}

export enum SortingDirection {
  ASC = 'ASC',
  DESC = 'DESC',
}

export enum SortingField {
  WTNSingles = 'ratings.ITF.scaledRating',
  WTNDoubles = 'ratings.ITFDBL.scaledRating',
  FamilyName = 'nativeFamilyName',
}

export interface Sorting {
  field: SortingField
  direction: SortingDirection
}

export type AnyFilter = '*'

export enum GenderFilter {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
  ANY = '',
}

export type CountryFilter = string[]

export type RangeFilter = { min: number; max: number }

export interface RatingFilter {
  type: RatingTypeEnum
  range: RangeFilter
  confidence: boolean
  gameZone?: RangeFilter
}

export interface AddressUI {
  addressType: string
  city: string
  countryCode: string
  state: string
}

export interface PlayerLocationUI {
  main: AddressUI
  secondary?: AddressUI[]
}

export interface GameZoneBounds {
  upper: number
  lower: number
}

export interface GameZones {
  [RatingTypeEnum.ITF]?: GameZoneBounds
  [RatingTypeEnum.ITFDBL]?: GameZoneBounds
}

export interface Player {
  id: string
  fullName?: string
  firstName?: string
  lastName?: string
  tennisID?: string
  wtnSingles?: string
  wtnDoubles?: string
  wtnConfidenceSingles?: number
  wtnConfidenceDoubles?: number
  wtnSinglesNumeric?: number | null
  wtnDoublesNumeric?: number | null
  gameZoneSingles?: GameZoneBounds
  gameZoneDoubles?: GameZoneBounds
  age?: number
  birthYear?: number
  birthDate?: string
  sex?: SexEnum
  country?: string
  countryCode?: string
  avatar?: string
  contemporaryWtn?: string
  contemporaryWtnConfidence?: number
  gender?: string
  residence?: string
  location?: PlayerLocationUI
  favouriteShot?: string
  height?: number
  preferredHand?: string
  backhand?: string
  optedOutOfPublicDataConsent?: boolean
  isRanked?: boolean
  eligibleForSelfSubmission?: boolean
  eligibleForPsq?: boolean
  lastPlayedSingles?: string
  lastPlayedDoubles?: string
  hasSubmittedResult?: boolean
  totalMatchUps?: number
}

export interface VsWinLikelihood {
  singles?: number
  doubles?: number
}
export interface VsInfo {
  players: [Player, Player]
  competedTotal: number
  wins: { p1: number; p2: number }
  winLikelihood: [VsWinLikelihood, VsWinLikelihood]
}

export interface GetVsInfoQueryResponse {
  persons: { items: [Partial<PersonAPI>, Partial<PersonAPI>]; totalItems: number }
  matchUpStatistics: {
    matchUpVsStatisticsCount: {
      totalMatches: number
      wins: number | null
      losses: number | null
    }
    winLikelihood: number | null
  }
}

export interface RatingsData {
  singles: any[]
  doubles: any[]
}

interface Bounds {
  min?: number
  max?: number
}

export interface DoublesSinglesBounds {
  doubles?: Bounds
  singles?: Bounds
}

export interface PlayerStats {
  playerId?: string
  lastPlayedSingles?: string
  lastPlayedDoubles?: string
  singlesInterval?: number
  doublesInterval?: number
  uniqueOpponents?: number
  wtnBoundsAllTime?: DoublesSinglesBounds
  wtnBoundsThisYear?: DoublesSinglesBounds
}

export interface Tiebreaker {
  loserPointsWon: number
  winnerPointsWon: number
}

export interface Match {
  id: string
  start: string
  score: Score
  status: MatchUpStatusEnum
  side1Players: Player[]
  side2Players: Player[]
}

export interface Set {
  winnerGamesWon: number
  loserGamesWon: number
  tiebreaker: Tiebreaker | null
}

export interface Score {
  sets: Set[]
  superTiebreak: Tiebreaker | null
}

export interface RecentMatch extends Match {
  didWin: boolean
  opponent: Player
  winLossRecord: string
}

export interface CommonMatch extends Match {
  tournament: string
  winner: Player
}

export interface Watchlist {
  id: string
  name: string
  players: Player[]
  playerIds: string[]
  stats: {
    avgSinglesWtn: string
    minSinglesWtn: string
    maxSinglesWtn: string
  }
}

export enum WatchlistModalAction {
  addPlayer = 'addPlayer',
  removePlayer = 'removePlayer',
}

export interface MatchUpsWinLoss {
  id: string
  wins: number
  losses: number
  start: string
}

export interface MatchUpStatBucket {
  matchesLost: number
  matchesWon: number
  month: number
  totalMatches: number
  year: number
}

export interface ProfileFormInput {
  nickname?: string
  preferredHand?: string
  favoriteShot?: string
  backhand?: string
  heightInMeters?: number
}

export enum AccountDetailsFetchType {
  REGISTRATION = 'REGISTRATION',
  COMPLETE = 'COMPLETE',
}

export enum CoreFieldKeyApi {
  NATIVE_GIVEN_NAME = 'nativeGivenName',
  NATIVE_FAMILY_NAME = 'nativeFamilyName',
  BIRTH_DATE = 'birthDate',
  SEX = 'sex',
  NATIONALITY_CODE = 'nationalityCode',
  COUNTRY_OF_RESIDENCE_CODE = 'countryOfResidenceCode',
}

export interface CoreAccountDetails {
  birthDate: string
  countryOfResidenceCode: string
  nationalityCode: string
  nativeGivenName: string
  nativeFamilyName: string
  sex: string
}

export interface AccountRegistrationDetails extends CoreAccountDetails {
  alreadyItfCustomer: boolean
  isCountryOfResidenceUpdateable: boolean
}

export interface AccountDetails extends CoreAccountDetails {
  isSexUpdateable: boolean
  isBirthDateUpdateable: boolean
  isNationalityUpdateable: boolean
  isNativeGivenNameUpdateable: boolean
  isNativeFamilyNameUpdateable: boolean
  isCountryOfResidenceUpdateable: boolean
  biographicalInformation: BiographicalInformation
  tennisId: string
}

export interface BasicPersonDetails {
  firstName: string
  lastName: string
  birthDate?: string
}

export interface WalkUpPersonDetails {
  nativeGivenName: string
  nativeFamilyName: string
  birthDate: string
  sex: SexEnum
  nationalityCode: string
  countryOfResidenceCode: string
  emailAddress: string
  signUpType: SignUpTypeEnum.WALKUP
  whereDidYouHearAboutUs?: WhereDidYouHearOptionEnum
  whereDidYouHearDetail?: string
  whereDidYouHearNa?: string
}

export type WalkUpPersonDetailsInput = WalkUpPersonDetails & {
  parentFullName?: string
  parentEmailAddress?: string
}

export type FastTrackPersonDetails = BasicPersonDetails & {
  id?: string
  alreadyRegisteredForService: boolean
  alreadyItfCustomer: boolean
}

export enum PersonIDEnum {
  ClubsparkID = 'ClubsparkID',
  ID = 'ID',
  NationalID = 'NationalID',
  TennisID = 'TennisID',
}

export interface PersonIDInput {
  identifier: string
  type: PersonIDEnum
}

export enum MatchUpTypeEnum {
  SINGLES = 'SINGLES',
  DOUBLES = 'DOUBLES',
}

export enum MatchUpStatusEnum {
  ABANDONED = 'ABANDONED',
  BYE = 'BYE',
  COMPLETED = 'COMPLETED',
  DEAD_RUBBER = 'DEAD_RUBBER',
  DEFAULTED = 'DEFAULTED',
  DISQUALIFIED = 'DISQUALIFIED',
  RETIRED = 'RETIRED',
  SUSPENDED = 'SUSPENDED',
  WALKOVER = 'WALKOVER',
}

export enum WorldTennisNumberTypeEnum {
  DOUBLE = 'DOUBLE',
  SINGLE = 'SINGLE',
}

export interface WorldTennisNumber {
  confidence: number
  gameZoneLower: number
  gameZoneUpper: number
  isRanked: boolean
  ratingDate: string
  source: string
  tennisNumber: number
  type: WorldTennisNumberTypeEnum
}

export interface MatchUpWorldTennisNumber extends WorldTennisNumber {
  personId: string
}

export interface Competitor extends Player {
  p1Win: number
  p1Loss: number
  p2Win: number
  p2Loss: number
}

export interface TiebreakUI {
  type: ScoreType
  points: Record<SideEnum, number>
}

export interface SetUI extends TiebreakUI {
  tiebreaker?: TiebreakUI
}

export type SidesUI = Record<SideEnum, Player[]>

export interface MatchUpUI {
  id: string
  start: string
  status: MatchUpStatusEnum
  type: MatchUpTypeEnum
  sides: SidesUI
  sets: SetUI[]
  winningSide: SideEnum | null
}

export interface SocialMatchUpUI extends MatchUpUI {
  submittedStatus: MatchUpSubmissionStateEnum
  submittedById: string
  reviewedById: string
  updatedAt: string
  createdAt: string
}

export interface Provider {
  id?: string
  providerId: string
  name: string
  orgId: string
  nationalityCodes?: string[]
  defaultOptOutStatus?: boolean
  personMinimumAge?: number
  personExternalUrlTemplate?: string
  logoUrl?: string
  displayName?: string
  landingPageUrl?: string
  supportPageUrl?: string
}

export interface ProviderList {
  items: Provider[]
  totalItems: number
}

export type TournamentUI = {
  id: string
  name: string
  start: string
  end: string
  results: MatchUpUI[]
  provider: Provider
  providerTournamentId: string
}

export interface Tournament {
  id: string
  name: string
  start: string
  end: string
}

export type ErrorMessages =
  | MessageTypes.NETWORK
  | MessageTypes.PLAYER_NOT_FOUND
  | MessageTypes.DEFAULT

export interface ErrorWithContext extends Error {
  status: number
  rayId: string | null
}

export enum WtnColorsEnum {
  PRIMARY = '#16234c',
  PRIMARY_ALT = '#003a70',
  SECONDARY = '#e03e52',
  SECONDARY_ALT = '#dc3741',
  TERTIARY = '#8f99b7',
  QUATERNARY = '#f6f7f9',
  WHITE = '#ffffff',
  GRAY = '#737680',
}

export enum WatchlistSortingKeys {
  PLAYERS = 'players',
}

export enum UserStatus {
  LOADING = 'LOADING',
  ACTIVE = 'ACTIVE',
  PENDING = 'PENDING',
  ERROR = 'ERROR',
  UNAUTHENTICATED = 'UNAUTHENTICATED',
}

export interface WtnConsents {
  commConsentItf: boolean
  commConsentNa: boolean
}

export enum ProfileSection {
  OVERVIEW = 'overview',
  RESULTS = 'results',
  H2H = 'h2h',
}

export enum ResultsSection {
  COMPETITIONS = 'competitions',
  SOCIAL_MATCHES = 'my-matches',
}

export interface SavedWtnInfo {
  dontShowMe: string[]
}

export interface StoredUser {
  userStatus: UserStatus
  tennisId: string
  analyticsId: string
}

export type CustomHTMLAttribute = { playerId: string; tennisId: string }

export type WithCustomHTMLAttribute<T extends keyof CustomHTMLAttribute> = Pick<
  CustomHTMLAttribute,
  T
>

export enum QueryParamNames {
  PROFILE_PLAYER_ID = 'id',
  PROFILE_TENNIS_ID = 'tennis-id',
  PROFILE_H2H_OPPONENT_ID = 'vsPlayerId',
  PROFILE_OWN_PROFILE_FLAG = 'own-profile',
  PROFILE_SECTION = 'section',
  PROFILE_RESULTS_SUB_SECTION = 'subSectionResults',
}

export enum ConstantQueryParamValues {
  PROFILE_OVERVIEW_SECTION = ProfileSection.OVERVIEW,
  PROFILE_RESULTS_SECTION = ProfileSection.RESULTS,
  PROFILE_H2H_SECTION = ProfileSection.H2H,
  PROFILE_COMPETITION_RESULTS_SUB_SECTION = ResultsSection.COMPETITIONS,
  PROFILE_SOCIAL_MATCHES_RESULTS_SUB_SECTION = ResultsSection.SOCIAL_MATCHES,
}

export const MY_SOCIAL_MATCHES_KEY = 'myself'

export enum GqlErrors {
  INVALID_ARGS = 'INVALID_ARGS',
  ITEM_NOT_FOUND = 'ITEM_NOT_FOUND',
  ITEM_DELETED = 'ITEM_DELETED',
  ITEM_APPROVED = 'ITEM_APPROVED',
  ITEM_REJECTED = 'ITEM_REJECTED',
}

export interface Option<T = string> {
  label: string
  value: T
}

export interface KeyItem<T = string> {
  label: string
  key: T
}

export type TimePeriodOption = KeyItem<number | null>

export type ManagePlayersInWatchlistsOperation = 'add' | 'remove' | 'create'

export type ManagePlayersInWatchlistsOperationResult = {
  type: ManagePlayersInWatchlistsOperation
} & (
  | {
      successful: true
      updatedWatchlistsIds: string[]
    }
  | {
      successful: false
      updatedWatchlistsIds: null
      error?: string
    }
)

export interface InteractiveNotification<TContext = Record<string, any>> {
  type: TypeOptions
  context: TContext
}

export interface ManagePlayerModalState {
  isOpen: boolean
  mode: AddOrManagePlayerModalMode
  player?: Player
  includedWatchlistsIds: string[]
}

export interface CsvColumnConfig<TItem> {
  header: string
  key: keyof TItem
  transformValue: (value: any, item: TItem) => string
}

export enum CustomEventWtn {
  APP_START = 'wtnAppStart',
  MENU_OPENING = 'wtnMenuOpening',
  REGISTRATION = 'wtnRegistration',
}

export type RatingHistory = Record<MatchUpTypeEnum, WorldTennisNumber[]>

export interface LineChartLabel {
  type: MatchUpTypeEnum
  name: string
  keyCol: number
}

export interface FeaturesConfig {
  canRegisterThroughWalkUp: boolean
}

export interface PsqResults {
  wtnSingles?: number
  wtnDoubles?: number
}
